import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '~/components/shared/Card/Card';
import CardHeader from '~/components/shared/CardHeader/CardHeader';
import CardSection from '~/components/shared/CardSection/CardSection';
import { SvgSrc } from '~/components/shared/Icon/Icon';
import useTranslate from '~/hooks/useTranslate';
import { Offer } from '~/Interfaces';
import { RootState } from '~/store/types/sharedTypes';
import * as offerActions from '~/store/actions/loanOfferActions';
import { formatAmount, getMarketCurrency } from '~/helpers/formatters.helper';
import { CampaignType, CountryCode } from '~/enums';
import DropDown from '~/components/shared/DropDown/DropDown';
import { generateOfferAmounts } from '~/helpers/generators.helper';
import Button, { ButtonType } from '~/components/shared/Button/button';
import PaymentCalculatorModal from '~/pages/LoanOffer/components/PaymentCalculator/PaymentCalculatorModal';
import useModals from '~/hooks/useModals';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import TermsSlider from './TermsSlider';
import AmountSlider from './AmountSlider';
import Congrats from './Congrats';

interface Props {
  data: Offer;
}

const LoanConfigurationCard: React.FC<Props> = ({ data }) => {
  /* States */
  const [maxAllowedTerm, setMaxAllowedTerm] = useState(data.maxAllowedTerm);

  /* Selectors */
  const {
    amount,
    toPayout,
    loanPeriod,
    monthlyFee,
    monthlyAmortization,
  } = useSelector((state: RootState) => state.loanOffer.userData);
  const {
    matchedMedia,
    intl: { market },
  } = useSelector((state: RootState) => state);
  const showSliders = market === CountryCode.SE || market === CountryCode.FI;
  const shouldMaxTermLogicApplies = false;

  /* Other hooks */
  const dispatch = useDispatch();
  const translate = useTranslate();
  const modals = useModals();

  const currency = getMarketCurrency()?.currency?.toLowerCase() ?? '';
  const exchangeRate = currency === 'eur' ? 0.1 : 1;

  const originalMinAmount = 10000 * exchangeRate;
  const sliderStep = 1000 * exchangeRate;
  const roundedCurrentAmountLeft =
    Math.ceil(data?.currentAmountLeft / originalMinAmount) * originalMinAmount;
  const roundedOriginalAmount =
    Math.ceil(data.amountOriginal / sliderStep) * sliderStep;
  const sliderMinAmount = Math.min(
    Math.max(originalMinAmount, roundedCurrentAmountLeft),
    data.maxAllowedAmount
  );
  const { campaignPrincipalFreeMonths = 0 } = data;
  // In the BackOffice, 3 months is the minimum loan period. Except for Finland where min term is 6 months.
  const defaultMinLoanTerm = market === CountryCode.FI ? 6 : 3;
  let minTerm = defaultMinLoanTerm;
  if (
    data.campaignType === CampaignType.AmortizationFreeMonths &&
    campaignPrincipalFreeMonths >= minTerm
  ) {
    minTerm = campaignPrincipalFreeMonths + 1;
  }
  const maxPossiblePayOut = data.maxAllowedAmount - data.currentAmountLeft;

  const calculateMaxAllowedTerm = (newAmount: number) => {
    if (!shouldMaxTermLogicApplies) return;

    // Here we set loan period to 12 months for loans below  100000SEK or 10000EUR
    if (newAmount <= 100000 * exchangeRate) setMaxAllowedTerm(12);
    // Here we set loan period to 18 months for loans below  150000SEK or 15000EUR
    else if (newAmount <= 150000 * exchangeRate) setMaxAllowedTerm(18);
    else setMaxAllowedTerm(24);

    if (loanPeriod > maxAllowedTerm)
      dispatch(
        offerActions.updateOfferData({
          loanPeriod: maxAllowedTerm,
        })
      );
  };

  /* Effects */
  useEffect(() => {
    const sliderDefaultAmount = Math.max(
      sliderMinAmount,
      Math.min(roundedOriginalAmount, data.maxAllowedAmount)
    );
    dispatch(
      offerActions.updateOfferData({
        amount: showSliders ? sliderDefaultAmount : data.maxAllowedAmount,
      })
    );
  }, [data.amountOriginal]);

  useEffect(() => {
    if (amount) {
      let loanPeriodWithAmortization = loanPeriod;
      if (data.campaignType === CampaignType.AmortizationFreeMonths) {
        loanPeriodWithAmortization -= data.campaignPrincipalFreeMonths;
      } else if (data.campaignType === CampaignType.FirstMonthFree) {
        loanPeriodWithAmortization -= 1;
      }
      dispatch(
        offerActions.updateOfferData({
          toPayout: Number(amount - data.currentAmountLeft),
          loanPeriod: loanPeriod || maxAllowedTerm,
          monthlyFee: Number((amount * data.interest) / 100),
          monthlyAmortization: Number(
            Math.round(amount / loanPeriodWithAmortization)
          ),
        })
      );
    }
  }, [amount, loanPeriod, data.currentAmountLeft]);

  useEffect(() => {
    if (showSliders) calculateMaxAllowedTerm(amount);
  }, [amount]);

  /* Handlers */

  const onAmountDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      offerActions.updateOfferData({
        amount: Number(e.currentTarget.value),
      })
    );
  };

  const onTermsDropDownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      offerActions.updateOfferData({
        loanPeriod: Number(e.currentTarget.value),
      })
    );
  };

  const generateTermsDropdownValues = useMemo(
    () => (maxAllowedTerms: number) => {
      const dropdownItems = [];
      const monthsTranslation = translate('Months');

      for (let index = minTerm; index <= maxAllowedTerms; index += 1) {
        dropdownItems.push({
          label: `${index} ${monthsTranslation}`,
          value: index,
        });
      }

      return dropdownItems;
    },
    []
  );

  const dropDownValues = useMemo(
    () =>
      generateOfferAmounts(
        data.currentAmountLeft,
        data.maxAllowedAmount,
        exchangeRate,
        market
      ),
    [data, currency]
  );

  const handleOpenPaymentScheduleModal = () => {
    modals.openModal({
      header: (
        <Typography fontWeight={FontWeights.Thick} fontSize={1.2}>
          {translate('PaymentCalculator.PaymentSchedule')}
        </Typography>
      ),
      children: (
        <PaymentCalculatorModal
          amount={amount}
          loanPeriod={loanPeriod}
          monthlyFee={monthlyFee}
          campaignType={data.campaignType}
          campaignDiscount={data.campaignDiscount}
          campaignPrincipalFreeMonths={data.campaignPrincipalFreeMonths}
          campaignInterestRateDiscount={data.campaignInterestRateDiscount}
        />
      ),
      footer: (
        <Button type={ButtonType.Outlined} onClick={modals.closeAll} mt={1}>
          {translate('Ok')}
        </Button>
      ),
      fullScreen: matchedMedia.smallScreenPortrait,
      filledBackground: true,
    });
  };

  const onAmountSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = Number(event.currentTarget.value);
    dispatch(
      offerActions.updateOfferData({
        amount: newAmount,
      })
    );
  };

  const onTermsSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = Number(event.currentTarget.value);

    dispatch(
      offerActions.updateOfferData({
        loanPeriod: Number(newTerm),
      })
    );
  };

  const shouldShowCongrats =
    maxPossiblePayOut > data.amountOriginal &&
    (market === CountryCode.SE || market === CountryCode.FI);

  return (
    <Card noPadding dataTestId="loan-offer-configuration-card">
      <CardHeader value={translate('LoanConfigurationCard.Title') as string} />
      {showSliders ? (
        <>
          {shouldShowCongrats && (
            <Congrats
              amountOriginal={data.amountOriginal}
              maxAllowedAmount={data.maxAllowedAmount}
            />
          )}

          <AmountSlider
            amount={amount}
            maxAllowedAmount={data.maxAllowedAmount}
            minAmount={sliderMinAmount}
            sliderStep={sliderStep}
            onAmountSliderChange={onAmountSliderChange}
          />

          <TermsSlider
            loanPeriod={loanPeriod}
            minTerm={minTerm}
            maxAllowedTerm={maxAllowedTerm}
            onTermsSliderChange={onTermsSliderChange}
          />
        </>
      ) : (
        <>
          <CardSection borderTop title={translate('ChooseAmount') as string}>
            <DropDown
              name="chooseAmount"
              defaultValue={data.maxAllowedAmount}
              onChange={onAmountDropDownChange}
              dropDownValues={dropDownValues}
            />
          </CardSection>

          <CardSection
            borderTop
            title={translate('LoanConfigurationCard.LoanPeriod') as string}
          >
            <DropDown
              name="loanPeriod"
              defaultValue={data.maxAllowedTerm}
              onChange={onTermsDropDownChange}
              dropDownValues={generateTermsDropdownValues(data.maxAllowedTerm)}
            />
          </CardSection>
        </>
      )}

      <CardSection>
        <Button
          type={ButtonType.Outlined}
          onClick={() => handleOpenPaymentScheduleModal()}
        >
          {translate('ViewPaymentSchedule')}
        </Button>
      </CardSection>

      {data.currentAmountLeft > 0 && (
        <CardSection
          title={translate('LoanConfigurationCard.ExistingCredit') as string}
          value={formatAmount(Math.round(data.currentAmountLeft))}
          iconSrc={SvgSrc.HandHoldingUsd}
          borderTop
          explanation={
            translate(
              'LoanConfigurationCard.ExistingCreditExplanation'
            ) as string
          }
        />
      )}
      <CardSection
        title={translate('NewLoanPayout') as string}
        value={formatAmount(Math.round(toPayout))}
        iconSrc={SvgSrc.MoneyBillWave}
        explanation={
          translate('LoanConfigurationCard.PayoutExplanation') as string
        }
        borderTop
      />
      <CardSection
        title={translate('MonthlyFee') as string}
        explanation={translate('MonthlyFeeExplanation') as string}
        value={formatAmount(Math.round(monthlyFee))}
        iconSrc={SvgSrc.Coins}
        borderTop
      />
      <CardSection
        title={translate('MonthlyAmortization') as string}
        explanation={translate('AmortizationExplanation') as string}
        value={formatAmount(Math.round(monthlyAmortization))}
        iconSrc={SvgSrc.ChartArea}
        borderTop
      />
      <CardSection
        title={translate('MonthlyTotal') as string}
        explanation={
          translate('LoanConfigurationCard.MonthlyTotalExplanation') as string
        }
        value={formatAmount(Math.round(monthlyAmortization + monthlyFee))}
        iconSrc={SvgSrc.ChartArea}
        borderTop
      />
      <CardSection
        title={translate('LoanConfigurationCard.PayoutDate') as string}
        value={translate('LoanConfigurationCard.PayoutDateValue') as string}
        iconSrc={SvgSrc.Clock}
        borderTop
      />
    </Card>
  );
};

export default LoanConfigurationCard;
